import React, { useCallback, useEffect, useState } from 'react';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { Loader2, RefreshCw, BarChart3 } from 'lucide-react';
import DateRangeFilter from '@/components/DateRangeFilter';
import { usageFetch, rangeQuery } from './usageApi';
import { UsageVisitsPanel } from './UsageVisitsPanel';
import { TopModulesCard } from './TopModulesCard';
import { TopUsersCard } from './TopUsersCard';
import { StickinessCard } from './StickinessCard';
import { UsageHeatmap } from './UsageHeatmap';
import { UserTimelineSheet } from './UserTimelineSheet';

const defaultRange = () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) });

export const UsageAnalyticsTab = () => {
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await usageFetch(`/overview${rangeQuery(range)}`));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl bg-[#161616] p-6 text-neutral-100 shadow-xl" data-testid="usage-analytics-tab">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-white">Analytics</h2>
        <p className="mt-1 text-sm text-neutral-400">Track active users, page views, module adoption and more.</p>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-white">
          <BarChart3 className="h-4 w-4 text-[#7ee8e2]" /> App visits
        </h3>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            value={range}
            onApply={setRange}
            buttonTestId="usage-date-range-trigger"
            triggerClassName="border-white/10 bg-[#232323] text-neutral-200 hover:bg-[#2a2a2a] hover:text-white"
          />
          <button
            onClick={load}
            disabled={loading}
            data-testid="usage-refresh-btn"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-[#232323] text-neutral-300 transition-colors hover:bg-[#2a2a2a]"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && <p className="mb-3 text-sm text-red-400" data-testid="usage-error">{error}</p>}

      {!data ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#7ee8e2]" /></div>
      ) : (
        <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <UsageVisitsPanel data={data} />
          <div className="grid gap-4 lg:grid-cols-2">
            <TopModulesCard modules={data.modules} />
            <TopUsersCard rows={data.leaderboard} onSelect={setSelected} />
            <StickinessCard kpis={data.kpis} />
            <UsageHeatmap cells={data.heatmap} />
          </div>
          <p className="text-[11px] text-neutral-600">
            Compared with {data.range.prev_start} → {data.range.prev_end}. Detailed events kept 180 days; daily summaries kept forever.
          </p>
        </div>
      )}

      <UserTimelineSheet user={selected} range={range} onClose={() => setSelected(null)} />
    </div>
  );
};
